import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { type ReactNode, useRef, useState } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button/Button';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';
import useKeymap from '@/hooks/pure/useKeymap';
import usePopupStore, {
  type PopupOptionT,
  type PopupThemeT,
} from '@/stores/pure/PopupStore';
import { modalStyle } from '@/styles/style';

import { Flex, Space } from '../base';
import { useModalContext } from '../Modal/Modal';

type PopupIconMap = {
  [key in PopupThemeT]: ReactNode;
};

const DefaultPopup = (props: PopupOptionT) => {
  const { closeModal } = useModalContext();
  const color = useColor();
  const { setPopup } = usePopupStore((state) => ({ setPopup: state.setPopup }));
  const [loading, setLoading] = useState(false);
  const [contentData, setContentData] = useState<any>(null);
  const confirmDataRef = useRef<any>(null);

  const popupIcon: PopupIconMap = {
    positive: (
      <CheckCircleOutlined style={{ color: color.primary100, fontSize: 16 }} />
    ),
    negative: (
      <ExclamationCircleOutlined
        style={{ color: color.red600, fontSize: 16 }}
      />
    ),
    neutral: (
      <ExclamationCircleOutlined
        style={{ color: color.gray600, fontSize: 16 }}
      />
    ),
  };

  const closePopup = async () => {
    await closeModal();
    setPopup(null);
  };

  const confirmHandler = async () => {
    setLoading(true);
    // renderContent가 있는 경우 getConfirmData로 데이터를 가져옴
    if (props.renderContent && props.getConfirmData) {
      confirmDataRef.current = props.getConfirmData();
    }
    await props.onConfirm?.(confirmDataRef.current || contentData);
    setLoading(false);
    closePopup();
  };

  const handleDataChange = (data: any) => {
    setContentData(data);
    confirmDataRef.current = data;
  };

  useKeymap('Enter', confirmHandler);

  useKeymap('Escape', closePopup);

  return (
    <Style style={props.style}>
      <Flex g="8px" a="flex-start">
        <Flex className="icon" j="center" a="center">
          {popupIcon[props.popupTheme]}
        </Flex>
        <Flex d="column" g="12px" f={1}>
          <Typo type="body1" weight="sb" color={color.gray700}>
            {props.title}
          </Typo>
          {props.renderContent
            ? props.renderContent({ onDataChange: handleDataChange })
            : props.message &&
              (typeof props.message === 'string' ? (
                <Typo type="body2" color={color.gray700}>
                  {props.message}
                </Typo>
              ) : (
                props.message
              ))}
        </Flex>
      </Flex>
      <Space h="24px" />
      <Flex j="space-between">
        <Flex style={{ paddingLeft: 20 }}></Flex>
        <Flex a="center" g="8px">
          {props.useBack && (
            <Button
              onClick={() => {
                closePopup();
              }}
              sizeType="m"
              style={{ minWidth: 80 }}
              variant="ghost"
            >
              Back
            </Button>
          )}
          {props.type === 'confirm' && (
            <Button
              onClick={() => {
                props.onCancel?.();
                closePopup();
              }}
              sizeType="m"
              style={{ minWidth: 100 }}
              variant="ghost"
              disabled={loading}
            >
              {props.cancelText || 'Cancel'}
            </Button>
          )}
          <Button
            onClick={confirmHandler}
            sizeType="m"
            style={{ minWidth: 100 }}
            variant={(() => {
              switch (props.popupTheme) {
                case 'negative':
                  return 'error';
                case 'positive':
                  return 'primary';
                case 'neutral':
                  return 'primary';
                default:
                  console.error('Invalid popup theme', props.popupTheme);
                  return 'primary';
              }
            })()}
            isLoading={loading}
          >
            {props.confirmText || 'Confirm'}
          </Button>
        </Flex>
      </Flex>
    </Style>
  );
};

const Style = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  ${modalStyle};
  width: 440px;
  background-color: ${(props) => props.theme.colors.gray100};
  padding: 24px;
  .icon {
    margin-top: 3px;
  }
`;

export default DefaultPopup;
